import type { TFunction } from "i18next";

import type {
  IExportColumn,
  IExportRow,
  IExportSpec,
} from "@krea/shared/utils/exportToXlsx";

import type { IEarlyRepaymentPlan } from "./earlyRepayment";
import type { IRepaymentPlanBidInfo, IRepaymentPlanEntryData } from "./types";
import type {
  IRepaymentPlanDisplay,
  MoneyColumn,
} from "./useRepaymentPlanDisplay";

type ExportRow = Record<string, number | string>;

export const buildRepaymentPlanExportSpec = ({
  bid,
  companyName,
  loanApplicationId,
  lenderName,
  earlyRepaymentFeeLabel,
  formatCurrency,
  formatPaybackTime,
  t,
  displayPlan,
  moneyColumns,
  cellValue,
  totalValue,
  hasFees,
}: {
  bid: IRepaymentPlanBidInfo;
  companyName: string | null;
  loanApplicationId: string | null;
  lenderName: string | null;
  earlyRepaymentFeeLabel: string;
  formatCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  t: TFunction;
  displayPlan: IEarlyRepaymentPlan;
  moneyColumns: MoneyColumn[];
  cellValue: IRepaymentPlanDisplay["cellValue"];
  totalValue: IRepaymentPlanDisplay["totalValue"];
  hasFees: boolean;
}): IExportSpec<ExportRow> => {
  const setupFeeLabel =
    bid.setupFee && bid.setupFee > 0
      ? formatCurrency(bid.setupFee)
      : t("bids.common.repayableWithoutFee");

  const titleRows = [
    ...(companyName ? [companyName] : []),
    `${t("bids.companyLoanBid.amount")}: ${formatCurrency(bid.amount)}`,
    `${t("bids.companyLoanBid.paybackTimeMonths")}: ${formatPaybackTime(bid.paybackTimeMonths)}`,
    `${t("bids.companyLoanBid.amortizationMethod")}: ${bid.amortizationMethodDisplayName}`,
    `${t("bids.companyLoanBid.setupFee")}: ${setupFeeLabel}`,
    `${t("bids.companyLoanBid.earlyRepaymentFee")}: ${earlyRepaymentFeeLabel}`,
  ];

  const columns: IExportColumn<ExportRow>[] = [
    { header: t("month"), accessor: "month", width: 24, alignment: "left" },
    ...moneyColumns.map((key) => ({
      header: t(key),
      accessor: key,
      width: 18,
      numberFormat: "#,##0.00",
      alignment: "right" as const,
    })),
  ];

  const valueKey = moneyColumns[0] ?? "payment";
  const labelled = (
    label: string,
    value: number | string,
  ): IExportRow<ExportRow> => ({ data: { month: label, [valueKey]: value } });
  const spacer: IExportRow<ExportRow> = { data: { month: "" } };

  const rows: IExportRow<ExportRow>[] = displayPlan.entries.map(
    (entry: IRepaymentPlanEntryData) => {
      const data: ExportRow = { month: entry.month };
      for (const key of moneyColumns) data[key] = cellValue(entry, key);
      return { data };
    },
  );

  const totalData: ExportRow = { month: t("total") };
  for (const key of moneyColumns) {
    const total = totalValue(key);
    totalData[key] = total === null ? "" : total;
  }
  rows.push({ data: totalData, isSummary: true });

  rows.push(spacer, { data: { month: t("totalsTitle") }, isSummary: true });
  rows.push(labelled(t("setupFee"), Number(displayPlan.totals.setupFee)));
  rows.push(
    labelled(
      t("totalCostOfCredit"),
      Number(displayPlan.totals.totalCostOfCredit),
    ),
  );
  rows.push(labelled(t("totalRepaid"), Number(displayPlan.totals.totalRepaid)));

  if (displayPlan.isEarly) {
    rows.push(spacer, { data: { month: t("savings.title") }, isSummary: true });
    rows.push(labelled(t("savings.interestSaved"), displayPlan.interestSaved));
    if (hasFees) {
      rows.push(labelled(t("savings.feesSaved"), displayPlan.feesSaved));
    }
    rows.push(labelled(t("savings.grossSaved"), displayPlan.grossSaved));
    rows.push(labelled(t("savings.earlyRepaymentFee"), earlyRepaymentFeeLabel));
  }

  // Slug filename segments so spaces/punctuation in the lender name don't
  // collide with the underscore-delimited pattern or break OS file handling.
  const slug = (value: string) =>
    value
      .trim()
      .replace(/[^\w]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const filename = `amortisationplan_${loanApplicationId ?? "unknown"}_offer_${
    lenderName ? slug(lenderName) : "lender"
  }_${bid.id}.xlsx`;

  return {
    filename,
    sheets: [{ sheetName: "Amortisation plan", titleRows, columns, rows }],
  };
};
